import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Radio, ListChecks, CheckCircle2, Eye, ShieldAlert, Trophy, Timer } from "lucide-react";
import { api } from "@/lib/api";

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="font-timer text-3xl font-bold mt-2" style={{ color }}>{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/admin/overview").then(({ data }) => setData(data)).catch(() => {});
      api.get("/admin/health").then(({ data }) => setHealth(data)).catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="text-slate-400 font-semibold py-20 text-center">Loading overview…</div>;

  const dot = (ok) => <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok === true ? "bg-[#138808]" : ok === false ? "bg-red-500" : "bg-amber-400"}`} />;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Competition at a glance.</p>
        </div>
        <Link to="/admin/live" data-testid="goto-live-monitor" className="px-5 py-2.5 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors inline-flex items-center gap-2">
          <Radio className="w-4 h-4" /> Live Monitor
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Registrations" value={data.total_registrations} color="#003366" testid="kpi-registrations" />
        <Kpi icon={Radio} label="Online Now" value={data.online_now} color="#138808" testid="kpi-online" />
        <Kpi icon={ListChecks} label="Questions Answered" value={data.questions_answered} color="#FF9933" testid="kpi-answers" />
        <Kpi icon={CheckCircle2} label="Completed Categories" value={data.completed_categories} color="#138808" testid="kpi-completed" />
        <Kpi icon={Eye} label="Visitors Today" value={data.unique_visitors_today} color="#003366" testid="kpi-visitors" />
        <Kpi icon={Trophy} label="Top Score" value={data.top_score} color="#FF9933" testid="kpi-topscore" />
        <Kpi icon={Timer} label="Avg Score" value={data.avg_score} color="#003366" testid="kpi-avgscore" />
        <Kpi icon={ShieldAlert} label="Anti-Cheat Events" value={data.anticheat_events} color="#DC2626" testid="kpi-anticheat" />
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Top 5 Players</h2>
          {data.leaderboard_top.length === 0 && <p className="text-sm text-slate-400">No scores yet.</p>}
          <div className="space-y-2">
            {data.leaderboard_top.map((e) => (
              <div key={e.user_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm font-semibold text-slate-700"><span className="font-timer text-slate-400 mr-2">#{e.rank}</span>{e.display_name}</span>
                <span className="font-timer font-bold text-[#FF9933]">{e.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">System Health</h2>
          {health ? (
            <div className="space-y-3 text-sm" data-testid="system-health">
              <div className="flex justify-between"><span className="text-slate-500 font-medium">API</span><span className="flex items-center gap-2 font-semibold">{dot(health.api === "ok")} Online</span></div>
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Database</span><span className="flex items-center gap-2 font-semibold">{dot(health.database === "ok")} {health.db_response_ms}ms</span></div>
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Email (SMTP)</span><span className="flex items-center gap-2 font-semibold">{dot(health.email === "configured" ? true : null)} {health.email === "configured" ? "Configured" : "Not configured"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Discord</span><span className="flex items-center gap-2 font-semibold">{dot(health.discord === "configured" ? true : null)} {health.discord === "configured" ? "Configured" : "Not configured"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Email failures (24h)</span><span className="font-semibold">{health.email_failures_24h}</span></div>
            </div>
          ) : <p className="text-sm text-slate-400">Checking…</p>}
        </div>
      </div>
    </div>
  );
}
